import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import gsap from 'gsap';
import { useLoginModal } from '../hooks/useLoginModal';
import { useAuth } from '../hooks/useAuth';
import { useSupportModal } from '../hooks/useSupportModal';
import { LifeBuoy, Eye, ArrowRight, ArrowUpRight } from 'lucide-react';
import { PLPLogo } from '../components/brand/PLPLogo';

const navLinks = [
  { label: 'Product', href: '#scoring' },
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Coverage', href: '#coverage' },
  { label: 'Data', href: '#sample-report' },
  { label: 'FAQ', href: '/#faq' },
];

export default function Navigation() {
  const navRef = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  // Read initial state from DOM: if native pre-hydration controller opened the menu, sync it
  const [mobileOpen, setMobileOpen] = useState(() => {
    if (typeof document !== 'undefined') {
      const menu = document.getElementById('mobile-nav-menu');
      if (menu) {
        const style = window.getComputedStyle(menu);
        if (style.display !== 'none') return true;
      }
    }
    return false;
  });
  const { open: openLogin } = useLoginModal();
  const { open: openSupport } = useSupportModal();
  const { isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    gsap.to(nav, { opacity: 1, duration: 0.6, delay: 0.3, ease: 'power3.out' });

    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLElement>, href: string) => {
    setMobileOpen(false);
    if (href.startsWith('/')) return;
    if (href.startsWith('#') && href.length > 1) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const announcementOffset = 'var(--announcement-height, 0px)';

  return (
    <nav
      ref={navRef}
      aria-label="Main navigation"
      className="fixed left-0 right-0 z-[105] opacity-0 transition-all duration-300 border-b border-transparent"
      style={{
        top: announcementOffset,
        height: 80,
        background: scrolled ? 'rgba(15, 25, 35, 0.95)' : 'rgba(15, 25, 35, 1.0)',
        backdropFilter: scrolled ? 'blur(16px)' : 'blur(8px)',
        borderColor: scrolled ? 'rgba(255,255,255,0.1)' : 'transparent',
        transition: 'top 0.3s ease, background 0.3s ease, backdrop-filter 0.3s ease, border-color 0.3s ease',
      }}
    >
      <div className="mx-auto flex h-full max-w-[1440px] items-center justify-between px-6">
        {/* Logo — White text on navy */}
        <Link to="/" className="flex items-center gap-2 transition-opacity duration-300 hover:opacity-80">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-black" style={{ background: '#F7F8F4', color: '#07110D' }}>PLP</div>
          <span className="text-sm font-bold tracking-tight text-[#F7F8F4]">PARCEL<span style={{ color: '#7CFF6B' }}>LEAD</span>PRO<sup className="text-[7px] ml-0.5 opacity-60">&trade;</sup></span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) =>
            link.href.startsWith('/') ? (
              <Link
                key={link.label}
                to={link.href}
                className="plp-nav-link"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="plp-nav-link"
              >
                {link.label}
              </a>
            )
          )}
          <button
            onClick={openSupport}
            className="plp-nav-link flex items-center gap-1.5"
          >
            <LifeBuoy size={14} />
            Support
          </button>
        </div>

        {/* Desktop Auth */}
        <div className="hidden items-center gap-4 md:flex">
          {isLoggedIn ? (
            <>
              <Link
                to="/dashboard"
                className="plp-nav-link"
              >
                My Account
              </Link>
              <button
                onClick={() => navigate('/pricing')}
                className="plp-btn-primary !px-4 !py-2 !text-xs flex items-center gap-1"
              >
                <ArrowUpRight size={12} /> Upgrade
              </button>
              <button
                onClick={logout}
                className="plp-btn-secondary !px-4 !py-2 !text-xs"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/dashboard?demo=true')}
                className="plp-btn-secondary !px-4 !py-2 !text-xs"
              >
                <Eye size={13} />
                View Demo
              </button>
              <button
                onClick={openLogin}
                className="plp-btn-secondary !px-4 !py-2 !text-xs"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/register')}
                className="plp-btn-primary !px-4 !py-2 !text-xs"
              >
                Start Free Trial
                <ArrowRight size={12} />
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger — always white on dark */}
        <button
          data-mobile-menu-toggle="true"
          className="flex flex-col gap-1.5 md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav-menu"
        >
          <span
            className="block h-0.5 w-6 bg-white transition-transform duration-300"
            style={{ transform: mobileOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none' }}
          />
          <span
            className="block h-0.5 w-6 bg-white transition-opacity duration-300"
            style={{ opacity: mobileOpen ? 0 : 1 }}
          />
          <span
            className="block h-0.5 w-6 bg-white transition-transform duration-300"
            style={{ transform: mobileOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none' }}
          />
        </button>
      </div>

      {/* Mobile Menu — always rendered so native pre-hydration controller can toggle it */}
      <div
        id="mobile-nav-menu"
        data-mobile-menu="true"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        className="absolute left-0 right-0 border-t border-white/10 px-6 py-6 md:hidden"
        style={{
          display: mobileOpen ? 'block' : 'none',
          top: 80,
          background: 'rgba(15, 25, 35, 0.98)',
          backdropFilter: 'blur(16px)',
        }}
      >
          <div className="flex flex-col gap-4">
            {navLinks.map((link) =>
              link.href.startsWith('/') ? (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="py-2 text-sm font-semibold text-[#F7F8F4] hover:text-[#F7F8F4]"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="py-2 text-sm font-semibold text-[#F7F8F4] hover:text-[#F7F8F4]"
                >
                  {link.label}
                </a>
              )
            )}
            <button
              onClick={() => { openSupport(); setMobileOpen(false); }}
              className="flex items-center gap-1.5 py-2 text-left text-sm font-semibold text-[#F7F8F4] hover:text-[#F7F8F4]"
            >
              <LifeBuoy size={14} /> Support
            </button>
            <div className="mt-2 flex flex-col gap-3 border-t border-white/10 pt-4">
              {isLoggedIn ? (
                <>
                  <Link
                    to="/dashboard"
                    className="py-2 text-sm font-semibold text-[#F7F8F4] hover:text-[#F7F8F4]"
                    onClick={() => setMobileOpen(false)}
                  >
                    My Account
                  </Link>
                  <button
                    onClick={() => { navigate('/pricing'); setMobileOpen(false); }}
                    className="plp-btn-primary w-full py-3 text-center text-sm flex items-center justify-center gap-1"
                  >
                    <ArrowUpRight size={14} /> Upgrade Now
                  </button>
                  <button
                    onClick={() => { logout(); setMobileOpen(false); }}
                    className="plp-btn-secondary w-full py-3 text-center text-sm"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { navigate('/dashboard?demo=true'); setMobileOpen(false); }}
                    className="plp-btn-secondary w-full py-3 text-center text-sm"
                  >
                    <Eye size={14} /> View Demo
                  </button>
                  <button
                    onClick={() => { openLogin(); setMobileOpen(false); }}
                    className="plp-btn-secondary w-full py-3 text-center text-sm"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => { navigate('/register'); setMobileOpen(false); }}
                    className="plp-btn-primary w-full py-3 text-center text-sm"
                  >
                    Start Free Trial
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
    </nav>
  );
}
